import type { Project_Bool_Exp } from "@/graphql";

import { ProjectFilterInput } from "../types/Project";

/**
 * The `where` for every project-listing document: the list query, the basic list query, and the
 * live subscription behind them.
 *
 * One object rather than a variable per filter, because an unset RELATIONSHIP filter has no value
 * that means "no condition". Hasura compiles `calculation: { … }` to
 * `EXISTS (SELECT 1 FROM project_calculation WHERE project_id = project.id AND …)`, and an empty
 * `{}` does not remove that EXISTS — it only makes it unconditional. So the previous shape, which
 * sent `{}` for every filter the reader had not set, quietly meant "only projects that HAVE a
 * calculation, a location and a status row": 68 of 651 projects on dev could not be listed at all,
 * including every project the start dialog had just created, since nothing writes a status row at
 * create time. Sending `null` instead is rejected outright by Hasura, and an omitted variable
 * coerces to the same null, so the key has to be absent from the object entirely — which is what
 * this builds.
 *
 * Scalar comparisons (`favorite`, `name`, `role`) never had the problem and are included the same
 * way only for consistency.
 */
export default function getProjectQueryWhere(
  filters: ProjectFilterInput | undefined,
  searchString?: string,
  tenantId?: string
): { where: Project_Bool_Exp } {
  const where: Project_Bool_Exp = { deletedBy: { _is_null: true } };

  // Redundant with Hasura's row-level tenant scoping by design (RTLCA-5588): RLS derives the
  // tenant from whichever JWT the request happened to carry, so a request that goes out under a
  // stale tenant's token would return that tenant's rows. With the explicit condition it returns
  // nothing instead.
  if (tenantId) where.tenantId = { _eq: tenantId };

  if (filters?.buildingTypeLabels.length) where.buildingType = { label: { _in: filters.buildingTypeLabels } };
  if (filters?.calculationTypeLabels.length) {
    where.calculation = { calculationType: { label: { _in: filters.calculationTypeLabels } } };
  }
  if (filters?.countries.length) where.location = { country: { _in: filters.countries } };
  if (filters?.projectStatuses.length) where.projectStatus = { id: { _in: filters.projectStatuses } };
  if (filters?.isFavorite) where.favorite = { _eq: true };
  if (filters?.roles.length) where.role = { _in: filters.roles };
  if (searchString?.trim()) where.name = { _ilike: `%${searchString.trim()}%` };

  return { where };
}
